import React, { useState } from "react";

import Image from "next/legacy/image";
import Link from "@/utils/link";

import HomeHeader from "@/components/home/HomeHeader";
import ArticleCard from "@/components/article/ArticleCard";
import { fetchGraphql } from "@/utils/query";

import TextField from "@/ui/TextField";
import Button from "@/ui/Button";

import Breadcrumbs from "@/components/common/Breadcrumbs";
import { imageLoader } from "@/utils/image";
import { ResourceLink } from "./index";

import SeoLinks from "@/components/home/SeoLinks";
import HomeFooter from "@/components/home/HomeFooter";

import HomeSearch from "@/components/home/HomeSearch";

const Faqs = ({ faqs, articles }) => {
    const [search, setSearch] = useState("");

    return (
        <div className="h-screen bg-gray-50">
            <HomeHeader fixed />

            <div className="container mx-auto pt-16 sm:pt-20 pb-4 px-16">
                <Breadcrumbs
                    links={[
                        { name: "Resources", href: "/resources" },
                        { name: "FAQs", href: "/resources/faq" },
                    ]}
                />
            </div>

            <div className="">
                <div className="relative w-full h-40 sm:h-96 bg-black">
                    <Image
                        loader={imageLoader}
                        src="https://cdn.purpledoorfinders.com/purpledoorfinders/purpledoorfinders-articles"
                        alt="purple-door-finders-faqs"
                        layout="fill"
                        objectFit="cover"
                        objectPosition="0px -90px"
                        priority
                        unoptimized={false}
                        className="opacity-80"
                    />

                    <div className="flex justify-center">
                        <h1 className="absolute px-8 top-6 sm:top-16 text-3xl sm:text-5xl text-white font-headline font-bold">
                            FAQs
                        </h1>

                        <div className="absolute bottom-8 sm:bottom-24 flex justify-center px-4">
                            <HomeSearch
                                inputClassName="w-[300px] sm:w-[500px]"
                                enableLocation={false}
                            />
                        </div>
                    </div>
                </div>

                <div className="container mx-auto px-8 sm:px-16">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-16">
                        <div className="md:col-span-2 py-8 flex flex-col space-y-4 max-w-4xl">
                            <h2 className="mb-4 text-xl font-bold">FAQs</h2>

                            <div className="flex justify-start items-center mb-8">
                                <TextField
                                    autoFocus={true}
                                    placeHolder="Search"
                                    fullwidth
                                    className="w-full"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                {search ? (
                                    <Button
                                        className="w-40 ml-4 mt-1"
                                        onClick={() => setSearch("")}
                                    >
                                        Clear Search
                                    </Button>
                                ) : null}
                            </div>

                            {faqs
                                .filter((f) => {
                                    if (search) {
                                        if (f.question) {
                                            if (
                                                f.question
                                                    .toLowerCase()
                                                    .includes(search.toLowerCase())
                                            ) {
                                                return true;
                                            }
                                        }

                                        if (f.answer) {
                                            if (
                                                f.answer
                                                    .toLowerCase()
                                                    .includes(search.toLowerCase())
                                            ) {
                                                return true;
                                            }
                                        }

                                        return false;
                                    } else {
                                        return true;
                                    }
                                })
                                .map((f, index, filteredFaqs) => (
                                    <React.Fragment key={f.question}>
                                        <div className="mt-4 px-4 py-4 bg-gray-200 rounded-md">
                                            <h3 className="font-bold">{f.question}</h3>
                                            <p className="mt-2">{f.answer}</p>
                                        </div>
                                        {(index + 1) % 5 === 0 &&
                                            index < filteredFaqs.length - 1 && (
                                                <div className="mt-0 mb-4">
                                                    <HomeSearch
                                                        inputClassName="w-[calc(100%-64px)] max-w-[500px]"
                                                        enableLocation={false}
                                                        higherInput={false}
                                                    />
                                                </div>
                                            )}
                                    </React.Fragment>
                                ))}
                        </div>

                        <div className="mt-8 sm:mt-8">
                            <h3 className="text-xl text-primary-500 font-headline font-bold">
                                Related Resources
                            </h3>

                            <div className="mt-4 w-full flex flex-col space-y-4 ">
                                <ResourceLink
                                    title="Senior Living Guidebooks"
                                    href="/resources/guidebooks"
                                />

                                <ResourceLink
                                    title="Senior Living Glossary"
                                    href="/resources/glossary"
                                />

                                <ResourceLink
                                    title="Frequently Asked Questions"
                                    href="/resources/faq"
                                />

                                <Link href="/resources">
                                    <a className="text-primary-500 text-sm uppercase font-black hover:underline">
                                        See All Resources
                                    </a>
                                </Link>
                            </div>

                            <div className="mt-8 sm:flex-shrink-0 sm:w-72">
                                <h3 className="text-xl text-primary-500 font-headline font-bold">
                                    Recent Articles
                                </h3>

                                <div className="mt-4 flex flex-col space-y-8">
                                    {articles
                                        ? articles.map((a, index) => (
                                              <ArticleCard
                                                  key={a ? a.title : index}
                                                  article={a}
                                              />
                                          ))
                                        : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <SeoLinks />
            <HomeFooter />
        </div>
    );
};

export const getServerSideProps = async () => {
    const result = await fetchGraphql(
        `query($findArticle: JSON, $sortArticle: JSON) {
            faqs {
                question
                question1
                question2
                question3
                answer
                keywords
                topic
            }
            articles(find: $findArticle, limit: 3, sort: $sortArticle) {
                articleId
                type
                newsroomType
                title
                date
                description
                mainImageUrl
                keywords
                topic
                author_link {
                    name
                }
            }
        }
    `,
        {
            findArticle: {
                newsroomType: { $nin: ["Newsroom", "Endorsement"] },
            },
            sortArticle: { _updatedAt: -1 },
        }
    );

    let articles = [];

    if (result.data.articles && result.data.articles.length > 0) {
        articles = result.data.articles;
    }

    return {
        props: {
            faqs: result.data.faqs && result.data.faqs.length > 0 ? result.data.faqs : [],
            articles,
            meta: {
                title: "FAQs | Purple Door",
                description: "Frequently Asked Questions on Purple Door",
            },
        },
    };
};

export default Faqs;
